import { ScaleTime } from 'd3-scale';

import { ANNOTATIONS_PADDING_BOTTOM } from './constants.js';
import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { AnnotationsTimeDisplay } from '../../../core/components/annotations/components/displays/AnnotationsTimeDisplay.js';
import { AnnotationsScrollWrapper } from '../../../core/components/annotations/components/overlays/AnnotationsScrollWrapper.js';
import {
  DEFAULT_INTEGRATION_TEXT_OVERFLOW,
  DEFAULT_SHOW_LABELS,
  SCROLLBAR_THRESHOLD,
} from '../../../core/components/annotations/constants.js';
import type { AnnotationsActionsPayload } from '../../../core/components/annotations/types/annotations.js';
import type { SelectionState } from '../../../core/components/annotations/types/state.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { useSetState } from '../../hooks/store/selectors.js';
import { useAnnotationsConfig } from '../../hooks/useAnnotationsConfig.js';
import { useXScales } from '../../hooks/useXScale.js';
import { AREAS } from '../../types/timeseries-grid-area-rules.js';

const COMPONENT_NAME = 'AnnotationsChart';

export const TimeseriesChartAnnotationsArea = () => {
  const config = useAnnotationsConfig();
  const {
    tracks,
    showLabels = DEFAULT_SHOW_LABELS,
    annotationsActions,
    tooltip,
  } = config;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(config);
  const setState = useSetState();
  const { currentScale } = useXScales();

  // TODO: TimeseriesChart needs to use d3-scale types instead of visx
  const scale = currentScale as ScaleTime<number, number>;

  const { width } = useGridPlotSize();

  const handleUpdateIndicators = (event: Partial<SelectionState>) => {
    setState((prev) => ({
      ...prev,
      annotations: {
        ...prev.annotations,
        ...event,
      },
    }));
  };

  return (
    <ChartGrid.GridArea
      paddingBottom={ANNOTATIONS_PADDING_BOTTOM}
      gridArea={AREAS.annotations}
    >
      {({ availableWidth, availableHeight }) => {
        const labelOffset = availableWidth - width;

        return (
          <AnnotationsScrollWrapper
            height={availableHeight}
            width={availableWidth + SCROLLBAR_THRESHOLD}
          >
            <AnnotationsTimeDisplay
              tracks={tracks}
              width={availableWidth}
              scale={scale}
              showLabels={showLabels ?? DEFAULT_SHOW_LABELS}
              labelsOffset={labelOffset}
              annotationsActions={(
                annotation: AnnotationsActionsPayload | undefined,
              ) => annotation && annotationsActions?.(annotation)}
              markerGroupHover={handleUpdateIndicators}
              textOverflow={DEFAULT_INTEGRATION_TEXT_OVERFLOW}
              tooltipConfig={tooltip}
              {...behaviorTrackingProps}
              data-dt-component={COMPONENT_NAME}
            />
          </AnnotationsScrollWrapper>
        );
      }}
    </ChartGrid.GridArea>
  );
};
